import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  IconButton,
  alpha
} from '@mui/material';
import { 
  Info as InfoIcon, 
  Download as DownloadIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Paleta profesional verde-azul
const COLORS = {
  gradient1: '#00b894',
  gradient2: '#0984e3',
  primary: '#0984e3',
  success: '#00b894',
  warning: '#eb9800ff',
  error: '#d63031',
  purple: '#6c5ce7',
  dark: '#2d3436'
};

const Stats = ({ label, value, color = "primary", icon: Icon }) => (
  <Chip 
    icon={Icon ? <Icon sx={{ fontSize: 16 }} /> : undefined}
    label={`${value} ${label}`}
    sx={{ 
      m: 0.5,
      bgcolor: alpha(COLORS[color] || COLORS.primary, 0.15),
      color: COLORS[color] || COLORS.primary,
      fontWeight: 600,
      '& .MuiChip-icon': { color: COLORS[color] || COLORS.primary }
    }}
  />
);

const DetailTable = ({ data }) => (
  <TableContainer 
    component={Paper} 
    elevation={0}
    sx={{ 
      mt: 2, 
      borderRadius: 2,
      border: '2px solid #e0e0e0'
    }}
  >
    <Table>
      <TableHead>
        <TableRow sx={{ bgcolor: alpha(COLORS.primary, 0.08) }}>
          <TableCell sx={{ fontWeight: 700, color: COLORS.dark }}>Usuario</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700, color: COLORS.dark }}>Total Intentos</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700, color: COLORS.dark }}>Aprobados</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700, color: COLORS.dark }}>Suspensos</TableCell>
          <TableCell sx={{ fontWeight: 700, color: COLORS.dark }}>Último Intento</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, color: COLORS.dark }}>Mejor</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, color: COLORS.dark }}>Media</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {data.map((user, index) => (
          <TableRow 
            key={index}
            sx={{ '&:hover': { bgcolor: alpha(COLORS.primary, 0.04) } }}
          >
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    bgcolor: alpha(COLORS.primary, 0.15),
                    color: COLORS.primary,
                    fontSize: '0.8rem',
                    fontWeight: 700
                  }}
                >
                  {user.username?.[0]?.toUpperCase()}
                </Avatar>
                <Typography sx={{ fontWeight: 500 }}>{user.username}</Typography>
              </Box>
            </TableCell>
            <TableCell align="center">
              <Chip 
                label={user.attempts.length} 
                size="small"
                sx={{ bgcolor: alpha(COLORS.primary, 0.1), color: COLORS.primary, fontWeight: 600 }}
              />
            </TableCell>
            <TableCell align="center">
              <Chip 
                label={user.passed} 
                size="small"
                sx={{ bgcolor: alpha(COLORS.success, 0.15), color: COLORS.success, fontWeight: 600 }}
              />
            </TableCell>
            <TableCell align="center">
              <Chip 
                label={user.failed} 
                size="small"
                sx={{ bgcolor: alpha(COLORS.error, 0.15), color: COLORS.error, fontWeight: 600 }}
              />
            </TableCell>
            <TableCell>
              {new Date(user.lastAttempt.attempt_date).toLocaleDateString('es-ES')}
            </TableCell>
            <TableCell align="right">
              <Typography sx={{ fontWeight: 600, color: user.bestScore >= 70 ? COLORS.success : COLORS.error }}>
                {user.bestScore}%
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography sx={{ fontWeight: 500 }}>
                {user.avgScore.toFixed(1)}%
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const CourseTestAnalytics = ({ data }) => {
  const [openDialog, setOpenDialog] = useState(false);

  const attempts = React.useMemo(() => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.attempts)) return data.attempts;
    return [];
  }, [data]);

  const stats = React.useMemo(() => {
    if (attempts.length === 0) return null;

    const userAttempts = attempts.reduce((acc, curr) => {
      if (!acc[curr.user_id]) {
        acc[curr.user_id] = {
          username: curr.username,
          attempts: [],
          bestScore: 0,
          avgScore: 0,
          passed: 0,
          failed: 0,
          lastAttempt: curr
        };
      }
      
      acc[curr.user_id].attempts.push(curr);
      acc[curr.user_id].bestScore = Math.max(acc[curr.user_id].bestScore, curr.score);
      acc[curr.user_id].avgScore = acc[curr.user_id].attempts.reduce((sum, a) => sum + a.score, 0) / 
                                  acc[curr.user_id].attempts.length;
      if (curr.passed) acc[curr.user_id].passed++;
      else acc[curr.user_id].failed++;
      
      if (new Date(curr.attempt_date) > new Date(acc[curr.user_id].lastAttempt.attempt_date)) {
        acc[curr.user_id].lastAttempt = curr;
      }

      return acc;
    }, {});

    const total = attempts.length;
    const totalPassed = attempts.filter(a => a.passed).length;
    const totalFailed = total - totalPassed;

    return {
      total,
      totalPassed,
      totalFailed,
      passRate: (totalPassed / total) * 100,
      uniqueUsers: Object.keys(userAttempts).length,
      userAttempts
    };
  }, [attempts]);

  const exportToExcel = () => {
    const userData = Object.values(stats.userAttempts)
      .map(user => ({
        'Usuario': user.username,
        'Total Intentos': user.attempts.length,
        'Aprobados': user.passed,
        'Suspensos': user.failed,
        'Mejor Puntuación': user.bestScore + '%',
        'Media': user.avgScore.toFixed(1) + '%',
        'Último Intento': new Date(user.lastAttempt.attempt_date).toLocaleDateString()
      }));

    const ws = XLSX.utils.json_to_sheet(userData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Resumen de Intentos');
    XLSX.writeFile(wb, 'resumen_intentos.xlsx');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const userData = Object.values(stats.userAttempts);
    
    doc.text('Resumen de Intentos por Usuario', 14, 15);
    
    doc.autoTable({
      startY: 25,
      head: [['Usuario', 'Total', 'Aprobados', 'Suspensos', 'Mejor', 'Media', 'Último']],
      body: userData.map(user => [
        user.username,
        user.attempts.length,
        user.passed,
        user.failed,
        user.bestScore + '%',
        user.avgScore.toFixed(1) + '%',
        new Date(user.lastAttempt.attempt_date).toLocaleDateString()
      ])
    });
    
    doc.save('resumen_intentos.pdf');
  };

  if (!stats) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 4,
          textAlign: 'center',
          borderRadius: 3,
          border: '2px dashed #e0e0e0'
        }}
      >
        <AnalyticsIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
        <Typography variant="h6" sx={{ color: '#636e72' }}>
          No hay datos de test disponibles
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Grid container spacing={3}>
        {/* Resumen General */}
        <Grid item xs={12} md={4}>
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: 3, 
              border: '2px solid #e0e0e0',
              height: '100%'
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <TrendingUpIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark }}>
                  Resumen Total
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#636e72' }}>
                    Tasa de Aprobación Global
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: stats.passRate >= 70 ? COLORS.success : COLORS.error }}>
                    {stats.passRate.toFixed(1)}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={stats.passRate}
                  sx={{ 
                    height: 10, 
                    borderRadius: 5,
                    bgcolor: alpha(stats.passRate >= 70 ? COLORS.success : COLORS.error, 0.15),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      background: stats.passRate >= 70 
                        ? `linear-gradient(90deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`
                        : COLORS.error
                    }
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                <Stats label="intentos" value={stats.total} color="primary" />
                <Stats label="aprobados" value={stats.totalPassed} color="success" icon={CheckCircleIcon} />
                <Stats label="suspensos" value={stats.totalFailed} color="error" icon={CancelIcon} />
              </Box>

              <Button
                variant="outlined"
                startIcon={<InfoIcon />}
                onClick={() => setOpenDialog(true)}
                fullWidth
                sx={{
                  borderColor: COLORS.primary,
                  color: COLORS.primary,
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: COLORS.primary,
                    bgcolor: alpha(COLORS.primary, 0.08)
                  }
                }}
              >
                Ver Detalles
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Tabla de últimos intentos */}
        <Grid item xs={12} md={8}>
          <Card 
            elevation={0}
            sx={{ 
              borderRadius: 3, 
              border: '2px solid #e0e0e0' 
            }}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.dark, mb: 2 }}>
                Últimos 5 Intentos
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(COLORS.primary, 0.05) }}>
                      <TableCell sx={{ fontWeight: 700 }}>Usuario</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Puntuación</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attempts.slice(0, 5).map((attempt, index) => (
                      <TableRow 
                        key={index}
                        sx={{ '&:hover': { bgcolor: alpha(COLORS.primary, 0.04) } }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar 
                              sx={{ 
                                width: 28, 
                                height: 28, 
                                bgcolor: alpha(COLORS.primary, 0.15),
                                color: COLORS.primary,
                                fontSize: '0.75rem',
                                fontWeight: 700
                              }}
                            >
                              {attempt.username?.[0]?.toUpperCase()}
                            </Avatar>
                            {attempt.username}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {new Date(attempt.attempt_date).toLocaleDateString('es-ES')}
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{ fontWeight: 600 }}>
                            {attempt.score}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={attempt.passed ? 'Aprobado' : 'Suspenso'}
                            size="small"
                            sx={{
                              bgcolor: alpha(attempt.passed ? COLORS.success : COLORS.error, 0.15),
                              color: attempt.passed ? COLORS.success : COLORS.error,
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Diálogo con detalles */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' }
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AnalyticsIcon />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Detalles de Intentos por Usuario
            </Typography>
          </Box>
          <IconButton onClick={() => setOpenDialog(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <DetailTable data={Object.values(stats.userAttempts)} />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button 
            variant="outlined"
            startIcon={<ExcelIcon />} 
            onClick={exportToExcel}
            sx={{
              borderColor: COLORS.success,
              color: COLORS.success,
              fontWeight: 600,
              borderRadius: 2,
              '&:hover': { borderColor: COLORS.success, bgcolor: alpha(COLORS.success, 0.08) }
            }}
          >
            Excel
          </Button>
          <Button 
            variant="outlined"
            startIcon={<PdfIcon />} 
            onClick={exportToPDF}
            sx={{
              borderColor: COLORS.error,
              color: COLORS.error,
              fontWeight: 600,
              borderRadius: 2,
              '&:hover': { borderColor: COLORS.error, bgcolor: alpha(COLORS.error, 0.08) }
            }}
          >
            PDF
          </Button>
          <Button 
            variant="contained"
            onClick={() => setOpenDialog(false)}
            sx={{
              background: `linear-gradient(135deg, ${COLORS.gradient1} 0%, ${COLORS.gradient2} 100%)`,
              fontWeight: 600,
              borderRadius: 2
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourseTestAnalytics;